import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch, type Ref } from "vue"
import { onBeforeRouteLeave } from "vue-router"
import type { ConflictResolver, FieldInfo, FormApi, SectionInfo } from "../types"
import { applyMerge, planMerge } from "../merge"
import { VersionConflictError } from "../versionConflict"
import { notifyError, notifySuccess } from "@/notifications"

export interface UseFormOptions<T> {
  initial: Ref<T>
  /** expectedVersion = Version des Stands, gegen den zusammengefuehrt wurde (nur mit loadCurrent). */
  save: (value: T, expectedVersion?: number) => Promise<void>
  /**
   * Laedt den aktuell gespeicherten Stand fuer den Konfliktschutz. Fehlt er (Neuanlage) oder
   * liefert er null, speichert save() ohne Zusammenfuehren.
   */
  loadCurrent?: () => Promise<{ value: T; version: number } | null>
  validate?: (value: T) => Record<string, string>
  /**
   * Whether the form can currently be saved (e.g. false while a publication lock disables
   * the save toast). Defaults to true. Gates the unsaved-changes warnings (beforeunload +
   * route-leave guard) — a form the user has no way to save must not warn them about
   * leaving with unsaved changes.
   */
  canSave?: Ref<boolean>
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value))
}

function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

export function useForm<T>(options: UseFormOptions<T>): FormApi<T> {
  const value = ref(deepClone(options.initial.value)) as Ref<T>
  const isSaving = ref(false)
  const isReady = ref(true)

  const sectionRegistry = shallowRef<SectionInfo[]>([])
  const fieldRegistry = shallowRef<FieldInfo[]>([])
  const serverErrors = ref<Record<string, string | null>>({})

  watch(options.initial, (next) => {
    value.value = deepClone(next)
  }, { deep: true })

  // Optional baseline captured by markSaved() after a successful save.
  // When set, takes precedence over `options.initial` for dirty diffing so
  // the leave-guard doesn't fire while we navigate to a post-save route.
  // Cleared automatically whenever `options.initial` changes (e.g. refetch).
  const savedBaseline = ref<T | null>(null) as Ref<T | null>
  watch(options.initial, () => { savedBaseline.value = null })

  const changedFieldNames = computed<string[]>(() => {
    const baseline = savedBaseline.value ?? options.initial.value
    const init = baseline as Record<string, unknown>
    const cur = value.value as unknown as Record<string, unknown>
    if (init === null || cur === null || typeof init !== "object" || typeof cur !== "object") {
      return deepEqual(init, cur) ? [] : ["__root__"]
    }
    const keys = new Set([...Object.keys(init), ...Object.keys(cur)])
    return [...keys].filter((k) => !deepEqual(init[k], cur[k]))
  })

  function markSaved() {
    savedBaseline.value = deepClone(value.value)
  }

  const isDirty = computed(() => changedFieldNames.value.length > 0)
  const changedCount = computed(() => changedFieldNames.value.length)
  const canSave = computed(() => options.canSave?.value ?? true)

  const formErrors = computed<Record<string, string>>(() =>
    options.validate ? options.validate(value.value) : {}
  )

  const isValid = computed(() =>
    fieldRegistry.value.every((f) => f.error.value === null) &&
    Object.keys(formErrors.value).length === 0
  )

  const sections = computed(() => sectionRegistry.value)
  const fields = computed(() => fieldRegistry.value)

  function reset() {
    value.value = deepClone(options.initial.value)
  }

  function setFieldError(name: string, message: string | null) {
    serverErrors.value = { ...serverErrors.value, [name]: message }
  }

  let conflictResolver: ConflictResolver | null = null
  function setConflictResolver(resolver: ConflictResolver | null) { conflictResolver = resolver }

  const MAX_CONFLICT_ATTEMPTS = 3

  function labelOf(key: string): string {
    const fields = fieldRegistry.value
    return (fields.find((f) => f.name === key) ?? fields.find((f) => f.dirtyField === key))?.label ?? key
  }

  /** null = Abbruch durch den Redakteur (nichts speichern). */
  async function mergeWith(base: T, current: T): Promise<T | null> {
    const plan = planMerge(base as object, value.value as object, current as object)
    let keepTheirs = new Set<string>()
    if (plan.conflicts.length > 0) {
      if (!conflictResolver) {
        notifyError({ title: "Speichern nicht moeglich", text: "Der Datensatz wurde inzwischen geaendert. Bitte neu laden." })
        return null
      }
      const conflicts = plan.conflicts.map((key) => ({
        key,
        label: labelOf(key),
        mine: (value.value as Record<string, unknown>)[key],
        theirs: (current as Record<string, unknown>)[key],
      }))
      const choices = await conflictResolver(conflicts)
      if (!choices) return null
      keepTheirs = new Set(plan.conflicts.filter((k) => choices[k] === "theirs"))
    }
    const registered = new Set(fieldRegistry.value.flatMap((f) => (f.dirtyField ? [f.name, f.dirtyField] : [f.name])))
    const silentlyTaken = plan.theirs.filter((k) => !plan.mine.includes(k) && registered.has(k))
    if (silentlyTaken.length > 0) {
      notifySuccess({
        title: "Aenderungen von anderen uebernommen",
        text: silentlyTaken.map(labelOf).join(", "),
      })
    }
    return applyMerge(value.value as object, current as object, plan.mine, keepTheirs) as T
  }

  async function save() {
    // Mass-touch before validating so errors become visible to the user.
    for (const field of fieldRegistry.value) {
      field.touched.value = true
    }
    if (!isValid.value) return

    isSaving.value = true
    try {
      if (!options.loadCurrent) {
        await options.save(value.value)
        return
      }
      // Basis = Stand beim Oeffnen; nach einem Merge der dabei geladene Stand (Spec, Schritt 7).
      let base = deepClone(savedBaseline.value ?? options.initial.value)
      for (let attempt = 1; ; attempt++) {
        let current: { value: T; version: number } | null
        try {
          current = await options.loadCurrent()
        } catch (err) {
          // Die Aufrufer (DirtySaveToast, Veroeffentlichen) verwerfen die Rejection still.
          notifyError(err as Error)
          throw err
        }
        if (!current) {
          await options.save(value.value)
          return
        }
        const merged = await mergeWith(base, current.value)
        if (merged === null) return
        value.value = merged
        base = deepClone(current.value)
        try {
          await options.save(merged, current.version)
          return
        } catch (err) {
          if (err instanceof VersionConflictError) {
            if (attempt < MAX_CONFLICT_ATTEMPTS) continue
            // notifyError ignoriert VersionConflictError — deshalb eine eigene Meldung.
            notifyError({ title: "Speichern nicht moeglich", text: "Der Datensatz wird gerade laufend geaendert. Bitte spaeter erneut speichern." })
          }
          throw err
        }
      }
    } finally {
      isSaving.value = false
    }
  }

  function handleBeforeUnload(e: BeforeUnloadEvent) {
    if (isDirty.value && canSave.value) {
      e.preventDefault()
      // Setting a non-empty string triggers the browser's native "unsaved changes" dialog.
      e.returnValue = "Es gibt ungespeicherte Änderungen. Wirklich verlassen?"
    }
  }

  onMounted(() => {
    window.addEventListener("beforeunload", handleBeforeUnload)
  })

  onBeforeUnmount(() => {
    window.removeEventListener("beforeunload", handleBeforeUnload)
  })

  // Vue Router leave guard — onBeforeRouteLeave is a no-op outside a router context,
  // safe to always call.
  try {
    onBeforeRouteLeave(() => {
      if (!isDirty.value || !canSave.value) return true
      return window.confirm("Es gibt ungespeicherte Änderungen. Wirklich verlassen?")
    })
  } catch {
    // Outside router context — leave guard is best-effort
  }

  const changedFieldNamesExposed = computed(() => changedFieldNames.value)

  const api: FormApi<T> = {
    value,
    isDirty,
    changedCount,
    changedFieldNames: changedFieldNamesExposed,
    isValid,
    isSaving,
    isReady: computed(() => isReady.value),
    save,
    reset,
    markSaved,
    setFieldError,
    serverErrors,
    formErrors,
    registerSection(s) { sectionRegistry.value = [...sectionRegistry.value, s] },
    unregisterSection(id) { sectionRegistry.value = sectionRegistry.value.filter(s => s.id !== id) },
    sections,
    registerField(f) { fieldRegistry.value = [...fieldRegistry.value, f] },
    unregisterField(name) { fieldRegistry.value = fieldRegistry.value.filter(f => f.name !== name) },
    fields,
    setConflictResolver
  }

  return api
}
